/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: DeleteEmployeeDialog.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-EMPLOYEES
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * DeleteEmployeeDialog - Frontend UI module: DeleteEmployeeDialog.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-13 - UX: обратная связь по ошибке удаления (Alert), loading-кнопка, защита закрытия во время запроса.
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: контракты функций, парные END_METHOD, блок удаления.
 * END_CHANGE_SUMMARY
 */

import { useState } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import RoleWorkloadBar from '../../../components/RoleWorkloadBar';
import { getColorByName } from '../../../utils/projectColors';

/* START_METHOD_DeleteEmployeeDialog */
/* START_CONTRACT: DeleteEmployeeDialog
 * PURPOSE: Диалог подтверждения удаления сотрудника.
 * INPUTS: { open: boolean, onClose: ()=>void, onConfirm: (id)=>Promise, employee: Employee }.
 * OUTPUTS: { JSX.Element|null - диалог }.
 * SIDE_EFFECTS: вызов onConfirm (HTTP наверх); локальный state.
 * LINKS: M-FE-EMPLOYEES
 * END_CONTRACT: DeleteEmployeeDialog
 */
export default function DeleteEmployeeDialog({ open, onClose, onConfirm, employee }) {
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  if (!employee) return null;

  /* START_METHOD_handleClose */
  /* START_CONTRACT: handleClose
   * PURPOSE: Сбросить ошибку и закрыть диалог (заблокировано во время удаления).
   * INPUTS: {}.
   * OUTPUTS: { void }.
   * SIDE_EFFECTS: setSubmitError; onClose.
   * LINKS: M-FE-EMPLOYEES
   * END_CONTRACT: handleClose
   */
  const handleClose = () => {
    if (loading) return;
    setSubmitError('');
    onClose();
  };
  /* END_METHOD_handleClose */

  /* START_METHOD_handleConfirm */
  /* START_CONTRACT: handleConfirm
   * PURPOSE: Удалить сотрудника; при успехе закрыть диалог, при ошибке показать сообщение.
   * INPUTS: {}.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: onConfirm (HTTP); setLoading; setSubmitError; onClose.
   * LINKS: M-FE-EMPLOYEES
   * END_CONTRACT: handleConfirm
   */
  const handleConfirm = async () => {
    if (loading) return;
    setLoading(true);
    setSubmitError('');
    try {
      await onConfirm(employee.id);
      /* START_BLOCK_DELETE_OK */
      setSubmitError('');
      onClose();
      /* END_BLOCK_DELETE_OK */
    } catch (err) {
      /* START_BLOCK_DELETE_FAIL */
      console.warn('[DeleteEmployeeDialog][handleConfirm][DELETE_FAIL] delete employee failed', {
        status: err.response?.status,
      });
      setSubmitError(
        err.response?.data?.message || 'Не удалось удалить сотрудника. Попробуйте ещё раз.'
      );
      /* END_BLOCK_DELETE_FAIL */
    } finally {
      setLoading(false);
    }
  };
  /* END_METHOD_handleConfirm */

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem' }}>Удаление сотрудника</DialogTitle>
      <DialogContent>
        <Collapse in={Boolean(submitError)} unmountOnExit>
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError('')}>
            {submitError}
          </Alert>
        </Collapse>
        <DialogContentText component="div">
          Удалить сотрудника «{employee.name}» ({employee.getRoleLabel()})? Это действие нельзя отменить.
          {employee.projectRoleName && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
              <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                Загрузка роли
              </Box>
              <RoleWorkloadBar
                value={employee.projectRoleWorkloadPercent}
                accentColor={getColorByName(employee.projectRoleName)}
                size="lg"
              />
            </Box>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button size="small" onClick={handleClose} disabled={loading}>
          Отмена
        </Button>
        <Button
          variant="contained"
          color="error"
          size="small"
          onClick={handleConfirm}
          loading={loading}
        >
          Удалить
        </Button>
      </DialogActions>
    </Dialog>
  );
}
/* END_METHOD_DeleteEmployeeDialog */
